import type { Placement, Currency } from '../lib/types';
import { formatMoney, formatDate } from '../lib/format';
import { explainSplit, splitForSelection, type Selection } from '../lib/splits';

interface Props {
  placement: Placement;
  viewerOid: string;
  selection: Selection;
  currency: Currency;
  onClose: () => void;
}

const LIKELY_LABEL: Record<string, { label: string; color: string }> = {
  expected: { label: 'Expected to extend', color: 'text-starter' },
  'not-expected': { label: 'Not expected to extend', color: 'text-finisher' },
  'not-discussed': { label: 'Not discussed', color: 'text-brand-subheader' },
  unsure: { label: 'Unsure', color: 'text-brand-subheader' },
};

export function ContractorDetail({
  placement: p,
  viewerOid,
  selection,
  currency,
  onClose,
}: Props) {
  const likely = p.likelyToExtend ? LIKELY_LABEL[p.likelyToExtend] : null;

  // Decide which split to show:
  //   - single OID scope (self or one person filtered) → that person's split
  //   - multi OID scope (team or leadership viewing all) → team aggregate
  const isSingleOid = selection.direct.size === 1 && selection.routed.size === 0;
  const singleOid = isSingleOid ? Array.from(selection.direct)[0] : null;
  const singleOwner = singleOid
    ? [p.owners.cro, p.owners.consultant, p.owners.ao, p.owners.conro].find((o) => o?.oid === singleOid) ?? null
    : null;

  let splitDisplay: React.ReactNode = null;
  if (isSingleOid && singleOid) {
    const bd = explainSplit(p, singleOid);
    if (bd.filledCount === 0 || bd.appearances === 0) {
      splitDisplay = (
        <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text-2)' }}>
          {singleOwner?.name ?? 'Selected user'}&rsquo;s split: <span style={{ color: 'var(--text-1)' }}>N/A</span>
        </div>
      );
    } else {
      splitDisplay = (
        <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text-2)' }}>
          {singleOwner?.name ?? 'Selected user'}&rsquo;s split:{' '}
          {formatMoney(p.wgp, currency, true)} × ({bd.appearances} ÷ {bd.filledCount}) ={' '}
          <span style={{ color: 'var(--text-1)', fontWeight: 600 }}>{formatMoney(bd.value, currency, true)}</span>
        </div>
      );
    }
  } else {
    const teamValue = splitForSelection(p, selection);
    if (teamValue === 0) {
      splitDisplay = (
        <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text-2)' }}>
          Team split: <span style={{ color: 'var(--text-1)' }}>N/A</span>
        </div>
      );
    } else {
      splitDisplay = (
        <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text-2)' }}>
          Team split:{' '}
          <span style={{ color: 'var(--text-1)', fontWeight: 600 }}>{formatMoney(teamValue, currency, true)}</span>
        </div>
      );
    }
  }

  const rate = (v: number | null) =>
    v ? `${formatMoney(v, currency, true)} / ${(p.frequency ?? 'day').toLowerCase()}` : '—';

  return (
    <div className="ds-modal-backdrop" onClick={onClose}>
      <div className="ds-modal" style={{ width: 'min(620px, 100%)', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <header style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 4, fontFamily: 'var(--mono)' }}>
              {p.idCode} · {p.status}
            </div>
            <div style={{ fontSize: 20, fontWeight: 500, letterSpacing: '-0.01em' }}>
              {p.candidateName ?? p.jobTitle}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 2 }}>
              {p.candidateName ? `${p.jobTitle} · ` : ''}{p.clientName ?? 'Unknown client'}
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ color: 'var(--text-3)', fontSize: 22, lineHeight: 1, alignSelf: 'flex-start' }}>
            ×
          </button>
        </header>

        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="ds-mini-grid">
            <Stat label="Start" value={formatDate(p.startDate)} />
            <Stat label="Original end" value={formatDate(p.originalEndDate)} />
            <Stat label="Actual end" value={p.actualEndDate ? formatDate(p.actualEndDate) : '—'} />
            <Stat label="Pay rate" value={rate(p.payRate)} />
            <Stat label="Charge rate" value={rate(p.chargeRate)} />
            <Stat label="Likely to extend" value={likely?.label ?? '—'} />
          </div>

          <div className="ds-panel" style={{ background: 'var(--sunken)' }}>
            <h3>
              Weekly gross profit
              <span style={{ fontFamily: 'var(--mono)', fontSize: 18, fontWeight: 700, color: 'var(--text-1)' }}>
                {formatMoney(p.wgp, currency, true)}
              </span>
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13 }}>
              <OwnerRow label="CRO" owner={p.owners.cro} viewerOid={viewerOid} />
              <OwnerRow label="Consultant" owner={p.owners.consultant} viewerOid={viewerOid} />
              <OwnerRow label="AO" owner={p.owners.ao} viewerOid={viewerOid} />
              <OwnerRow label="CONRO" owner={p.owners.conro} viewerOid={viewerOid} />
            </div>
            <div style={{ borderTop: '1px solid var(--border)', marginTop: 12, paddingTop: 12 }}>{splitDisplay}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      {label}
      <b>{value}</b>
    </div>
  );
}

function OwnerRow({
  label,
  owner,
  viewerOid,
}: {
  label: string;
  owner: { oid: string; name: string } | null;
  viewerOid: string;
}) {
  const isMe = owner?.oid === viewerOid;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span style={{ color: 'var(--text-3)' }}>{label}</span>
      <span style={{ color: isMe ? 'var(--text-1)' : 'var(--text-2)', fontWeight: isMe ? 500 : 400 }}>
        {owner?.name ?? '—'}
        {isMe && <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--text-3)' }}>(you)</span>}
      </span>
    </div>
  );
}